import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import { Button } from '@ui/components/ui/button';
import { Input } from '@ui/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  selectItemsByValue,
} from '@ui/components/ui/select';
import { cardHeaderClass, cardHeaderTitleClass, cardShellClass, tableFrameClass } from '@app/constants/styles';
import {
  AGREEMENT_OPTIONS,
  AGREEMENT_TERMS,
  FLAT_FEE,
  type FlatFee,
  OUT_OF_HOURS_BANDS,
  OutOfHoursBand,
  PRIORITIES,
  Priority,
  SLA_TARGETS,
  ServiceHoursDraft,
  SlaTarget,
  SupportAgreement,
  draftFromAgreement,
  draftFromServiceHours,
  asHours,
  asRate,
  formatHours,
  formatRate,
  invalidServiceFields,
  readServiceHours,
  serviceHoursFor,
  serviceHoursFromDraft,
  writeServiceHours,
} from './serviceLevels';
import { useReportUnsaved } from './unsavedWork';
import { type CellRange, numberFieldHolds, rangeContainer, rangeOverlay, useCellRange } from './cellRange';
import CellMenu from './CellMenu';
import SprintScrollRail from './SprintScrollRail';

// The same rules the cost matrix above is drawn with, so the two grids on this tab read as one thing.
const cell = 'h-10 border-b border-r border-border last:border-r-0';
const padded = `${cell} px-4 max-md:px-3`;
const headCell = `${padded} bg-muted/40 text-sm font-medium text-foreground`;
// A tint rather than a box: the grid's own rules are already the cell's border.
const askable =
  'cursor-text outline-none hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset';
// A field flush inside its cell, which is how every grid on these tabs is typed into.
const flushField =
  'h-full rounded-none border-0 px-4 text-right focus-visible:ring-2 focus-visible:ring-inset [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

/** Part of the block so a table copies whole; neither can be typed into. */
const HEAD_ROW = '__head__';
const NAME_COL = '__name__';
/** The multipliers' column, in both grids: the targets' other columns are the targets themselves. */
const RATE_COL = 'rate';

/** The picker alone until an agreement is picked, which fills the card from that agreement's terms. */
const ServiceHours = ({ canEdit, version }: { canEdit: boolean; version?: string }): JSX.Element => {
  const { t } = useTranslation();
  const { productId = '' } = useParams<{ productId: string }>();

  // Bumped on save, because the agreement lives outside React in the product store.
  const [revision, setRevision] = useState(0);
  /** The card as typed, once anything has been typed or an agreement picked. Null reads what is sold. */
  const [draft, setDraft] = useState<ServiceHoursDraft | FlatFee | null>(null);
  /** The bands last on screen, so a pass over Flat fee does not reset them to the defaults. */
  const [heldBands, setHeldBands] = useState<ServiceHoursDraft['rates'] | null>(null);
  /** The one field being typed into: a field is a figure until it is asked for. */
  const [active, setActive] = useState<string | null>(null);
  // One flag for both grids, so a failure in one is not still on screen after a copy in the other.
  const [copyFailed, setCopyFailed] = useState(false);
  // One flag again: a block clears only its own, so the card cannot read both and OR them.
  const [costPasteRefused, setCostPasteRefused] = useState(false);

  const sold = useMemo(() => readServiceHours(productId, version), [productId, version, revision]);

  // An agreement is per product and per version, and typing belongs to the one it was typed into.
  useEffect(() => {
    setDraft(null);
    setHeldBands(null);
    setActive(null);
  }, [productId, version]);

  /** What is on screen: the typing if there is any, else what was sold, else nothing has been. */
  const soldDraft = sold && sold.agreement !== FLAT_FEE ? draftFromServiceHours(sold) : sold;
  const fields: ServiceHoursDraft | FlatFee | null = draft ?? soldDraft;
  /** The agreement's terms on screen; null under a flat fee, which has none to show. */
  const tiered = fields && fields.agreement !== FLAT_FEE ? fields : null;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(soldDraft);
  const invalid = tiered ? invalidServiceFields(tiered) : [];
  const canSave = dirty && invalid.length === 0;

  // Whatever would take the reader off this tab or this version asks before it costs them this typing.
  useReportUnsaved('serviceHours', dirty);

  // The bands survive a pick and the multipliers do not: only the bands are the same under every agreement.
  const chooseAgreement = (agreement: SupportAgreement | typeof FLAT_FEE): void => {
    if (agreement === FLAT_FEE) {
      if (tiered) setHeldBands(tiered.rates);
      setDraft({ agreement: FLAT_FEE });
      return;
    }
    const bands = tiered?.rates ?? heldBands;
    setDraft(bands ? draftFromAgreement(agreement, bands) : draftFromServiceHours(serviceHoursFor(agreement)));
  };

  const edit = (change: (current: ServiceHoursDraft) => ServiceHoursDraft): void => {
    if (!tiered) return;
    setDraft(change(tiered));
  };
  const typeRate = (band: OutOfHoursBand, value: string): void =>
    edit(current => ({ ...current, rates: { ...current.rates, [band]: value } }));
  const typePriorityRate = (priority: Priority, value: string): void =>
    edit(current => ({ ...current, priorityRates: { ...current.priorityRates, [priority]: value } }));
  const typeTarget = (priority: Priority, target: SlaTarget, value: string): void =>
    edit(current => ({
      ...current,
      targets: { ...current.targets, [priority]: { ...current.targets[priority], [target]: value } },
    }));

  /** Anything outside the table it lands in is dropped rather than written somewhere else. */
  const typeRates = (cells: { row: string; col: string; value: string }[]): void =>
    edit(current => {
      const rates = { ...current.rates };
      cells.forEach(({ row, value }) => {
        if (OUT_OF_HOURS_BANDS.includes(row as OutOfHoursBand)) rates[row as OutOfHoursBand] = value;
      });
      return { ...current, rates };
    });
  const typeTargets = (cells: { row: string; col: string; value: string }[]): void =>
    edit(current => {
      const targets = { ...current.targets };
      const priorityRates = { ...current.priorityRates };
      cells.forEach(({ row, col, value }) => {
        if (!PRIORITIES.includes(row as Priority)) return;
        if (col === RATE_COL) priorityRates[row as Priority] = value;
        else if (SLA_TARGETS.includes(col as SlaTarget))
          targets[row as Priority] = { ...targets[row as Priority], [col as SlaTarget]: value };
      });
      return { ...current, targets, priorityRates };
    });

  /** A block apiece, read plainly: "2 h" arrives in a spreadsheet cell as text, and text does not add up. */
  const rateBlock = useCellRange({
    rows: [HEAD_ROW, ...OUT_OF_HOURS_BANDS],
    cols: [NAME_COL, RATE_COL],
    readCell: (row, col) => {
      if (row === HEAD_ROW) return col === NAME_COL ? t('serviceHours.outOfHours') : t('serviceHours.rate');
      if (col === NAME_COL) return t(`serviceHours.bands.${row}.name`);
      return tiered?.rates[row as OutOfHoursBand] ?? '';
    },
    writeCells: canEdit ? typeRates : undefined,
    isEditable: (row, col) => canEdit && row !== HEAD_ROW && col !== NAME_COL,
    resetOn: `${productId}:${version ?? ''}`,
    // A blank rate or target is not a value this card can save, so there is nothing to empty.
    allowClear: false,
    onCopyResult: setCopyFailed,
    onCostPasteResult: setCostPasteRefused,
  });
  const targetBlock = useCellRange({
    rows: [HEAD_ROW, ...PRIORITIES],
    cols: [NAME_COL, ...SLA_TARGETS, RATE_COL],
    readCell: (row, col) => {
      if (row === HEAD_ROW) {
        if (col === NAME_COL) return t('serviceHours.priority');
        return col === RATE_COL ? t('serviceHours.rate') : t(`serviceHours.${col}`);
      }
      if (col === NAME_COL) return t(`serviceHours.priorities.${row}`);
      if (col === RATE_COL) return tiered?.priorityRates[row as Priority] ?? '';
      return tiered?.targets[row as Priority]?.[col as SlaTarget] ?? '';
    },
    writeCells: canEdit ? typeTargets : undefined,
    isEditable: (row, col) => canEdit && row !== HEAD_ROW && col !== NAME_COL,
    resetOn: `${productId}:${version ?? ''}`,
    // A blank rate or target is not a value this card can save, so there is nothing to empty.
    allowClear: false,
    onCopyResult: setCopyFailed,
    onCostPasteResult: setCostPasteRefused,
  });

  const discard = (): void => {
    setDraft(null);
    setHeldBands(null);
    setActive(null);
    // The banners describe the last press, so leaving them up outlives what they are about.
    setCopyFailed(false);
    setCostPasteRefused(false);
    rateBlock.clear();
    targetBlock.clear();
  };

  const save = (): void => {
    if (!draft) return;
    writeServiceHours(productId, draft.agreement === FLAT_FEE ? draft : serviceHoursFromDraft(draft), version);
    discard();
    setRevision(current => current + 1);
  };

  const agreementOptions = AGREEMENT_OPTIONS.map(agreement => ({
    value: agreement,
    label: t(`serviceHours.agreements.${agreement}`),
  }));

  const agreementPicker = (): JSX.Element => (
    <Select
      // Null, not undefined: the kit reads undefined as uncontrolled and warns on the first pick.
      value={fields?.agreement ?? null}
      disabled={!canEdit}
      onValueChange={next => next && chooseAgreement(next as SupportAgreement | typeof FLAT_FEE)}
      items={selectItemsByValue(agreementOptions)}
    >
      <SelectTrigger size="sm" className="bg-card shadow-xs" aria-label={t('serviceHours.agreement')}>
        {/* base-ui's Value renders what is picked and nothing where nothing is, so the asking is ours. */}
        {fields ? <SelectValue /> : <span className="text-muted-foreground">{t('serviceHours.choose')}</span>}
      </SelectTrigger>
      {/* Squarely on top of the trigger, and always downwards, as every picker on this tab opens. */}
      <SelectContent className="min-w-0" align="start" alignItemWithTrigger={false}>
        {agreementOptions.map(option => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  /** A marked cell hiding what it holds would leave nothing to correct. */
  const rateReads = (value: string): string => {
    const rate = asRate(value);
    return rate === null ? value : t('serviceHours.rateValue', { rate: formatRate(rate) });
  };
  const hoursReads = (value: string): string => {
    const hours = asHours(value);
    return hours === null ? value : t('serviceHours.hoursValue', { hours: formatHours(hours) });
  };

  /** What a cell needs to be part of its table's block: the wash where it is in one, and the gestures. */
  const inBlock = (block: CellRange, row: string, col: string) => ({
    className: block.selected(row, col) ? rangeOverlay(block.edges(row, col)) : undefined,
    ...block.cellHandlers(row, col),
  });

  /** A cell of one of the two grids: the figure, until it is landed on. */
  const figureCell = (
    block: CellRange,
    row: string,
    col: string,
    key: string,
    label: string,
    reads: string,
    value: string,
    step: string,
    onType: (value: string) => void,
  ): JSX.Element => {
    const typing = active === key;
    const bad = invalid.includes(key);
    const open = (): void => {
      if (!canEdit) return;
      setActive(key);
      // The cell being typed into is also where a pasted block would land.
      block.anchorAt(row, col);
    };
    return (
      <td
        key={key}
        // Focusable, so the card can be filled in from the keyboard alone.
        tabIndex={typing || !canEdit ? undefined : 0}
        onFocus={typing ? undefined : open}
        // A drag or shift-click has already claimed the press for the block.
        onClick={typing ? undefined : () => !block.claimedClick() && open()}
        {...(typing ? block.openCellHandlers(row, col) : block.cellHandlers(row, col))}
        onKeyDown={
          typing
            ? undefined
            : event => {
                if (event.key !== 'Enter' && event.key !== ' ') return;
                // Space scrolls the page otherwise, and this card is scrolled past.
                event.preventDefault();
                open();
              }
        }
        className={cx(
          typing ? cell : `${padded} text-right`,
          !typing && askable,
          !typing && bad && 'bg-destructive/10',
          !typing && block.selected(row, col) && rangeOverlay(block.edges(row, col)),
          typing && block.selected(row, col) && rangeOverlay(block.edges(row, col), { open: true }),
        )}
      >
        {typing ? (
          <Input
            {...(numberFieldHolds(value)
              ? { type: 'number', min: 0, step }
              : { type: 'text', inputMode: 'decimal' as const })}
            aria-label={label}
            value={value}
            autoFocus
            onBlur={() => setActive(null)}
            // The way back out to the grid: while a cell is open the field owns the keyboard.
            onKeyDown={event => {
              if (event.key !== 'Escape' && event.key !== 'Enter') return;
              event.preventDefault();
              event.currentTarget.blur();
            }}
            // A number field takes the wheel as input, and this card is scrolled past.
            onWheel={event => event.currentTarget.blur()}
            onChange={event => onType(event.target.value)}
            className={cx(
              flushField,
              bad ? 'bg-destructive/10 hover:bg-destructive/15' : 'bg-muted/20 hover:bg-muted/40',
            )}
          />
        ) : (
          <span className="text-sm tabular-nums text-muted-foreground">{reads}</span>
        )}
      </td>
    );
  };

  return (
    <section className={cardShellClass}>
      <div className={cardHeaderClass}>
        <div className={cardHeaderTitleClass}>
          <h2 className="text-base font-semibold text-foreground">{t('serviceHours.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('serviceHours.hint')}</p>
          {/* A line, not a table of one row: the window is the agreement's, not a product's to set. */}
          {tiered && (
            <p className="mt-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{t('serviceHours.window')}</span>{' '}
              <span className="tabular-nums">
                {t('serviceHours.windowValue', {
                  from: AGREEMENT_TERMS[tiered.agreement].from,
                  to: AGREEMENT_TERMS[tiered.agreement].to,
                  days: AGREEMENT_TERMS[tiered.agreement].days,
                })}
              </span>
            </p>
          )}
          {tiered && AGREEMENT_TERMS[tiered.agreement].alwaysOnTopPriorities && (
            <p className="mt-1 text-xs text-muted-foreground">{t('serviceHours.alwaysOn')}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 print:hidden">
          {/* Where every other picker on this tab sits, from the first pick onwards. */}
          {agreementPicker()}
          {/* Held rather than hidden where a field is not what it asks for, so the card says why. */}
          {canEdit && dirty && (
            <>
              <Button variant="ghost" size="sm" onClick={discard}>
                {t('budget.discard')}
              </Button>
              <Button size="sm" disabled={!canSave} onClick={save}>
                {t('actions.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Nothing to draw until the picker is answered, nor under a flat fee: the rest is an agreement's terms. */}
      {!tiered && (
        <p className="px-5 max-md:px-4 pb-5 text-sm text-muted-foreground">
          {t(fields ? 'serviceHours.flatFee' : 'serviceHours.none')}
        </p>
      )}
      {tiered && (
        <>
          {/* The box holds the block, so the keys and the clipboard are its while no cell is open. */}
          <div
            ref={rateBlock.containerHandlers.ref}
            tabIndex={rateBlock.containerHandlers.tabIndex}
            onCopy={active ? undefined : rateBlock.containerHandlers.onCopy}
            // A block closes the cell it landed on, so it is read as figures, not through the field.
            onPaste={event => {
              rateBlock.containerHandlers.onPaste(event);
              if (event.defaultPrevented) setActive(null);
            }}
            onKeyDown={active ? undefined : rateBlock.containerHandlers.onKeyDown}
            className={`mx-5 mb-4 overflow-hidden rounded-xl border border-border max-md:mx-4 ${rangeContainer}`}
          >
            <table className="w-full table-fixed border-collapse [&>tbody>tr:last-child>*]:border-b-0">
              <thead>
                <tr>
                  <th
                    scope="col"
                    {...inBlock(rateBlock, HEAD_ROW, NAME_COL)}
                    className={cx(headCell, 'text-left', inBlock(rateBlock, HEAD_ROW, NAME_COL).className)}
                  >
                    {t('serviceHours.outOfHours')}
                  </th>
                  <th
                    scope="col"
                    {...inBlock(rateBlock, HEAD_ROW, RATE_COL)}
                    className={cx(headCell, 'w-[26%] text-right', inBlock(rateBlock, HEAD_ROW, RATE_COL).className)}
                  >
                    {t('serviceHours.rate')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {OUT_OF_HOURS_BANDS.map(band => {
                  const name = t(`serviceHours.bands.${band}.name`);
                  const detail = t(`serviceHours.bands.${band}.detail`);
                  return (
                    <tr key={band}>
                      {/* Beside the name, not under it, so every row is 40px and the two grids line up. */}
                      <th
                        scope="row"
                        {...inBlock(rateBlock, band, NAME_COL)}
                        className={cx(
                          headCell,
                          'border-r text-left font-normal',
                          inBlock(rateBlock, band, NAME_COL).className,
                        )}
                      >
                        <span className="flex min-w-0 items-baseline gap-2">
                          <span className="shrink-0 text-sm text-foreground">{name}</span>
                          <span className="truncate text-xs text-muted-foreground" title={detail}>
                            {detail}
                          </span>
                        </span>
                      </th>
                      {figureCell(
                        rateBlock,
                        band,
                        RATE_COL,
                        `rate:${band}`,
                        `${name}, ${t('serviceHours.multiplier')}`,
                        rateReads(tiered.rates[band]),
                        tiered.rates[band],
                        '0.05',
                        value => typeRate(band, value),
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div
            ref={targetBlock.containerHandlers.ref}
            tabIndex={targetBlock.containerHandlers.tabIndex}
            onCopy={active ? undefined : targetBlock.containerHandlers.onCopy}
            onPaste={event => {
              targetBlock.containerHandlers.onPaste(event);
              if (event.defaultPrevented) setActive(null);
            }}
            onKeyDown={active ? undefined : targetBlock.containerHandlers.onKeyDown}
            className={cx(tableFrameClass, 'no-scrollbar mb-5', rangeContainer)}
          >
            <table className="w-full min-w-[34rem] table-fixed max-md:min-w-[22rem] border-collapse [&>tbody>tr:last-child>*]:border-b-0">
              <thead>
                <tr>
                  <th
                    scope="col"
                    {...inBlock(targetBlock, HEAD_ROW, NAME_COL)}
                    className={cx(
                      headCell,
                      'w-[30%] text-left max-md:w-16',
                      inBlock(targetBlock, HEAD_ROW, NAME_COL).className,
                    )}
                  >
                    {t('serviceHours.priority')}
                  </th>
                  {SLA_TARGETS.map(target => (
                    <th
                      key={target}
                      scope="col"
                      {...inBlock(targetBlock, HEAD_ROW, target)}
                      className={cx(headCell, 'text-right', inBlock(targetBlock, HEAD_ROW, target).className)}
                    >
                      {t(`serviceHours.${target}`)}
                    </th>
                  ))}
                  <th
                    scope="col"
                    {...inBlock(targetBlock, HEAD_ROW, RATE_COL)}
                    className={cx(headCell, 'w-[18%] text-right', inBlock(targetBlock, HEAD_ROW, RATE_COL).className)}
                  >
                    {t('serviceHours.rate')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {PRIORITIES.map(priority => (
                  <tr key={priority}>
                    <th
                      scope="row"
                      {...inBlock(targetBlock, priority, NAME_COL)}
                      className={cx(headCell, 'text-left', inBlock(targetBlock, priority, NAME_COL).className)}
                    >
                      {t(`serviceHours.priorities.${priority}`)}
                    </th>
                    {SLA_TARGETS.map(target =>
                      figureCell(
                        targetBlock,
                        priority,
                        target,
                        `${priority}:${target}`,
                        `${t(`serviceHours.priorities.${priority}`)}, ${t(`serviceHours.targets.${target}`)}`,
                        hoursReads(tiered.targets[priority][target]),
                        tiered.targets[priority][target],
                        '0.5',
                        value => typeTarget(priority, target, value),
                      ),
                    )}
                    {figureCell(
                      targetBlock,
                      priority,
                      RATE_COL,
                      `priorityRate:${priority}`,
                      `${t(`serviceHours.priorities.${priority}`)}, ${t('serviceHours.multiplier')}`,
                      rateReads(tiered.priorityRates[priority]),
                      tiered.priorityRates[priority],
                      '0.05',
                      value => typePriorityRate(priority, value),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <SprintScrollRail scroller={targetBlock.containerHandlers.ref} columns={0} />
          </div>
        </>
      )}

      {/* One figure to a cell, so one Copy, and Clear only where the block can be typed into. */}
      {[rateBlock, targetBlock].map((block, index) => (
        <CellMenu
          key={index === 0 ? 'rates' : 'targets'}
          at={block.menuAt}
          onClose={block.closeMenu}
          items={[
            { label: t('cells.copy'), onSelect: () => block.copyAs('lead') },
            // No Clear: an empty rate or target cannot be saved, so emptying one is a dead end.
          ]}
        />
      ))}

      {copyFailed && <p className="px-5 max-md:px-4 pb-4 text-sm text-destructive">{t('cells.copyFailed')}</p>}
      {/* Refused because it holds money: the grids here hold hours and a rate per hour, never a total. */}
      {costPasteRefused && (
        <p className="px-5 max-md:px-4 pb-4 text-sm text-destructive">{t('cells.costPasteRefused')}</p>
      )}
    </section>
  );
};

export default ServiceHours;
